import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { LoginResult, SessionRecord } from '@luogen/shared';
import * as authApi from '@/api/auth';
import {
  AUTH_CARD_KEY,
  AUTH_DEVICE_KEY,
  AUTH_SESSION_KEY,
  AUTH_USER_ID_KEY,
  SESSION_RECORD_KEY,
} from '@/api/storage-keys';

const TEN_DAYS = 10 * 24 * 60 * 60 * 1000;

/** 演示会话：后端 AUTH_DEV_BYPASS=true 时任意 token 放行，前端不再拦登录 */
const DEMO_SESSION: SessionRecord = {
  account: 'demo',
  userId: 'dev-user',
  cardKey: 'dev',
  sessionToken: 'dev',
  deviceId: 'dev-device',
  expiry: Date.now() + TEN_DAYS,
};

/** 登录态：只存"我是谁"，不存任何业务数据 */
export const useSessionStore = defineStore('session', () => {
  const account = ref('');
  const userId = ref('');
  const cardKey = ref(localStorage.getItem(AUTH_CARD_KEY) ?? '');
  const sessionToken = ref(localStorage.getItem(AUTH_SESSION_KEY) ?? '');
  const deviceId = ref(localStorage.getItem(AUTH_DEVICE_KEY) ?? '');
  const expiry = ref(0);

  const isLoggedIn = computed(() => Boolean(cardKey.value && sessionToken.value && userId.value));

  function hydrate() {
    // 没有历史登录 → 直接给演示会话，避免卡在登录弹窗
    try {
      if (!localStorage.getItem(SESSION_RECORD_KEY)) {
        account.value = DEMO_SESSION.account;
        userId.value = DEMO_SESSION.userId;
        cardKey.value = DEMO_SESSION.cardKey;
        sessionToken.value = DEMO_SESSION.sessionToken;
        deviceId.value = DEMO_SESSION.deviceId;
        expiry.value = DEMO_SESSION.expiry;
        localStorage.setItem(SESSION_RECORD_KEY, JSON.stringify(DEMO_SESSION));
        return;
      }
    } catch {
      /* 隐私模式下写不进去就忽略，继续走正常流程 */
    }
    const raw = localStorage.getItem(SESSION_RECORD_KEY);
    if (!raw) return;
    try {
      const record = JSON.parse(raw) as Partial<SessionRecord>;
      account.value = record.account ?? '';
      userId.value = record.userId ?? '';
      cardKey.value = record.cardKey ?? '';
      sessionToken.value = record.sessionToken ?? '';
      deviceId.value = record.deviceId ?? '';
      expiry.value = record.expiry ?? 0;
    } catch {
      localStorage.removeItem(SESSION_RECORD_KEY);
    }
  }

  function applyLogin(result: LoginResult, loginAccount: string) {
    account.value = loginAccount;
    userId.value = result.user_id;
    cardKey.value = result.card_key;
    sessionToken.value = result.session_token;
    deviceId.value = result.device_id;
    expiry.value = Date.now() + TEN_DAYS;

    localStorage.setItem(AUTH_CARD_KEY, cardKey.value);
    localStorage.setItem(AUTH_SESSION_KEY, sessionToken.value);
    localStorage.setItem(AUTH_USER_ID_KEY, userId.value);
    localStorage.setItem(AUTH_DEVICE_KEY, deviceId.value);
    localStorage.setItem(
      SESSION_RECORD_KEY,
      JSON.stringify({
        account: account.value,
        userId: userId.value,
        cardKey: cardKey.value,
        sessionToken: sessionToken.value,
        deviceId: deviceId.value,
        expiry: expiry.value,
      } satisfies SessionRecord),
    );
  }

  async function requestCode(target: string, type: 'sms' | 'email') {
    return authApi.sendCode({ account: target, code_type: type });
  }

  async function signIn(target: string, code: string) {
    const result = await authApi.login({ account: target, code });
    if (result.code !== 200 || !result.data) throw new Error(result.msg || '登录失败');
    applyLogin(result.data, target);
    return result;
  }

  function clear() {
    account.value = '';
    userId.value = '';
    cardKey.value = '';
    sessionToken.value = '';
    expiry.value = 0;
    for (const key of [AUTH_CARD_KEY, AUTH_SESSION_KEY, AUTH_USER_ID_KEY, SESSION_RECORD_KEY]) {
      localStorage.removeItem(key);
    }
  }

  return { account, userId, cardKey, sessionToken, deviceId, expiry, isLoggedIn, hydrate, signIn, requestCode, clear };
});
